import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { useModal } from './AppModal';
import { supabaseData } from './supabaseData';
import { AlertTriangle, Package, DollarSign, TrendingDown, ChevronRight, Clock } from 'lucide-react';

export default function InventoryHealthWidget({ companyId, onNavigate }) {
  const { colors } = useTheme();
  const { showError } = useModal();

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [summary, setSummary] = useState({ critical: 0, warning: 0, outOfStock: 0, restockCost: 0 });
  const [burnItems, setBurnItems] = useState([]);
  const [hasItems, setHasItems] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);

      // Load company settings
      const { data: company, error: compErr } = await supabaseData
        .from('companies')
        .select('inventory_enabled, inventory_default_warning_threshold, inventory_default_critical_threshold')
        .eq('id', companyId)
        .single();

      if (compErr) throw compErr;
      if (!company?.inventory_enabled) {
        setEnabled(false);
        setLoading(false);
        return;
      }
      setEnabled(true);

      const defaultWarning = company.inventory_default_warning_threshold ?? 1.5;
      const defaultCritical = company.inventory_default_critical_threshold ?? 0.5;

      // Load inventory items, stock, alert configs, and recent transactions in parallel
      // Get default location for stock lookup
      const { data: defaultLoc } = await supabaseData
        .from('locations')
        .select('id')
        .eq('company_id', companyId)
        .eq('is_default', true)
        .single();

      if (!defaultLoc) {
        setLoading(false);
        return;
      }

      const [itemsRes, stockRes, alertsRes, txnRes] = await Promise.all([
        supabaseData
          .from('inventory_items')
          .select('id, name, reorder_point, reorder_quantity, unit_cost')
          .eq('company_id', companyId)
          .eq('is_active', true),
        supabaseData
          .from('inventory_stock')
          .select('inventory_item_id, quantity_on_hand')
          .eq('company_id', companyId)
          .eq('location_id', defaultLoc.id),
        supabaseData
          .from('inventory_alert_configs')
          .select('inventory_item_id, warning_threshold, critical_threshold')
          .eq('company_id', companyId),
        supabaseData
          .from('inventory_transactions')
          .select('inventory_item_id, quantity_change')
          .eq('company_id', companyId)
          .eq('transaction_type', 'usage')
          .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (stockRes.error) throw stockRes.error;
      if (alertsRes.error) throw alertsRes.error;
      if (txnRes.error) throw txnRes.error;

      const items = itemsRes.data || [];
      if (items.length === 0) {
        setHasItems(false);
        setLoading(false);
        return;
      }
      setHasItems(true);

      // Build lookup maps
      const stockMap = {};
      for (const s of (stockRes.data || [])) {
        stockMap[s.inventory_item_id] = (stockMap[s.inventory_item_id] || 0) + (s.quantity_on_hand || 0);
      }

      const alertMap = {};
      for (const a of (alertsRes.data || [])) {
        alertMap[a.inventory_item_id] = a;
      }

      // Usage per item (last 30 days)
      const usageMap = {};
      for (const t of (txnRes.data || [])) {
        usageMap[t.inventory_item_id] = (usageMap[t.inventory_item_id] || 0) + Math.abs(t.quantity_change || 0);
      }

      let critical = 0;
      let warning = 0;
      let outOfStock = 0;
      let restockCost = 0;
      const burnRateItems = [];

      for (const item of items) {
        const qty = stockMap[item.id] || 0;
        const rp = item.reorder_point || 0;
        const alertCfg = alertMap[item.id];
        const warnThresh = (alertCfg?.warning_threshold ?? defaultWarning) * rp;
        const critThresh = (alertCfg?.critical_threshold ?? defaultCritical) * rp;

        if (qty === 0) {
          outOfStock++;
        }

        if (qty <= critThresh && rp > 0) {
          critical++;
          restockCost += (item.reorder_quantity || 0) * (item.unit_cost || 0);
        } else if (qty <= warnThresh && rp > 0) {
          warning++;
          restockCost += (item.reorder_quantity || 0) * (item.unit_cost || 0);
        }

        // Burn rate calc
        const used30 = usageMap[item.id] || 0;
        if (rp > 0 && used30 > 0 && qty > 0) {
          const dailyBurn = used30 / 30;
          const daysRemaining = Math.round(qty / dailyBurn);
          burnRateItems.push({
            id: item.id,
            name: item.name,
            qty,
            dailyBurn: dailyBurn.toFixed(1),
            daysRemaining
          });
        }
      }

      // Sort by urgency (fewest days remaining first), take top 5
      burnRateItems.sort((a, b) => a.daysRemaining - b.daysRemaining);

      setSummary({ critical, warning, outOfStock, restockCost });
      setBurnItems(burnRateItems.slice(0, 5));
    } catch (err) {
      console.error('InventoryHealthWidget error:', err);
      showError('Failed to load inventory health data');
    } finally {
      setLoading(false);
    }
  }, [companyId, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Render nothing if not enabled or no items
  if (!enabled || !hasItems || loading) return null;

  const { critical: critCount, warning: warnCount, outOfStock: oosCount, restockCost } = summary;

  // If everything is fine and no burn items, don't clutter dashboard
  if (critCount === 0 && warnCount === 0 && oosCount === 0 && burnItems.length === 0) return null;

  const cards = [
    {
      label: 'Critical',
      value: critCount,
      icon: AlertTriangle,
      color: colors.error,
      bg: colors.error + '18'
    },
    {
      label: 'Warning',
      value: warnCount,
      icon: AlertTriangle,
      color: colors.warning,
      bg: colors.warning + '18'
    },
    {
      label: 'Out of Stock',
      value: oosCount,
      icon: Package,
      color: '#a855f7',
      bg: '#a855f718'
    },
    {
      label: 'Est. Restock Cost',
      value: `$${restockCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: DollarSign,
      color: colors.info,
      bg: colors.info + '18'
    }
  ];

  const getDaysColor = (days) => {
    if (days <= 3) return colors.error;
    if (days <= 7) return colors.warning;
    if (days <= 14) return '#f59e0b';
    return colors.textMuted;
  };

  return (
    <div style={{
      background: colors.bgCard,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`,
      padding: isMobile ? '0.75rem' : '1rem',
      marginBottom: '1rem'
    }}>
      {/* Header */}
      <div
        onClick={() => onNavigate && onNavigate('inventory')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
          cursor: onNavigate ? 'pointer' : 'default'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Package size={18} color={colors.accent} />
          <span style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: colors.text
          }}>Inventory Health</span>
        </div>
        {onNavigate && (
          <ChevronRight size={16} color={colors.textMuted} />
        )}
      </div>

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? '0.5rem' : '0.75rem',
        marginBottom: burnItems.length > 0 ? '0.75rem' : 0
      }}>
        {cards.map((card) => (
          <div key={card.label} style={{
            background: card.bg,
            borderRadius: '8px',
            padding: isMobile ? '0.5rem' : '0.625rem 0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <card.icon size={isMobile ? 16 : 18} color={card.color} />
            <div>
              <div style={{
                fontSize: isMobile ? '1rem' : '1.125rem',
                fontWeight: '700',
                color: card.color,
                lineHeight: 1.2
              }}>{card.value}</div>
              <div style={{
                fontSize: '0.7rem',
                color: colors.textMuted,
                lineHeight: 1.2
              }}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Burn Rate Section */}
      {burnItems.length > 0 && (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            marginBottom: '0.5rem'
          }}>
            <TrendingDown size={14} color={colors.textMuted} />
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: colors.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.03em'
            }}>Days Until Stockout</span>
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.375rem'
          }}>
            {burnItems.map((item) => (
              <div key={item.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.375rem 0.5rem',
                background: colors.bgInput,
                borderRadius: '6px',
                fontSize: '0.8rem'
              }}>
                <span style={{
                  color: colors.text,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginRight: '0.75rem'
                }}>{item.name}</span>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexShrink: 0
                }}>
                  <span style={{
                    color: colors.textMuted,
                    fontSize: '0.75rem'
                  }}>{item.qty} left</span>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}>
                    <Clock size={12} color={getDaysColor(item.daysRemaining)} />
                    <span style={{
                      color: getDaysColor(item.daysRemaining),
                      fontWeight: '600',
                      fontSize: '0.8rem',
                      minWidth: '2rem',
                      textAlign: 'right'
                    }}>
                      {item.daysRemaining}d
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
